import React, {useState} from 'react'

import {useApiWithSiteId} from '@src/hooks/useApiWithSiteId'

import {Button, Inline, Card, Loading,Pagination} from '@therms/atalaya'
import {FormikText} from '@src/components/FormikFields/FormikText'
import {FormikSelectPositions} from '@src/modules/Positions/shared/Fields/FormikSelectPositions'
import {Form, Formik} from 'formik'
import {FormikTextarea} from '@src/components/FormikFields/FormikTextarea'
import SingleAnnouncementHistory from './components/SingleAnnouncementHistory'
import {useAuth} from '@src/modules/Auth/shared/state'
import {delErrorCheck} from '@src/services/api-resp-checks'
import {Alerts} from '@src/components/Alerts'
import {CreateAnnouncement, DeleteAnnouncement, GetAnnouncements, ModifyAnnouncement} from '@src/services/api-client'

function AnnouncementsIndex() {
  const [isDeleting, setIsDeleting] = useState(false)
  const [showNewAnnouncement, setShowNewAnnouncement] = useState(false)
  const [selectedIdx, setSelectedIdx] = useState(null)
  const [limit, setLimit] = useState(25)
  const [skippedRecords, setSkippedRecords] = useState(0)
  const [recordsPerPage, setRecordsPerPage] = useState(7)
  const {currentSite} = useAuth()
  const siteId = currentSite.id

  const {call, data, isCalling} = useApiWithSiteId(GetAnnouncements, {
    siteId,
    offset: skippedRecords,
    limit: recordsPerPage
  })

  // Find the selected message (for editing)
  const selectedMessage = selectedIdx !== null
    ? data.announcements.find(msg => msg.id === selectedIdx)
    : null

  // Handler for saving (add or edit)
  const handleSave = async (values, {resetForm}) => {
    if (!values.title || !values.body || !values.notifyPositionIds.length) return
    if (selectedIdx !== null) {
      // Edit mode
      try {
        await ModifyAnnouncement({
          title: values.title,
          body: values.body,
          positions: values.notifyPositionIds,
          id: selectedMessage.id,
          siteId: currentSite.id
        })
        Alerts.success(`Announcement updated successfully`)
        call()
      } catch (error) {
        Alerts.error('There was a problem modifying an Announcement', error.message)
      }
      setSelectedIdx(null)
    } else {
      // New announcement
      try {
        await CreateAnnouncement({
          title: values.title,
          body: values.body,
          siteId: currentSite.id,
          positions: values.notifyPositionIds
        })
        Alerts.success(`Announcement sent!`)

        call()
        resetForm()
        setShowNewAnnouncement(false)
      } catch (err) {
        Alerts.error('There was a problem sending an Announcement', err.message)
      }
    }
  }

  // Handler for delete (direct, like PositionForm)
  const handleDelete = idx => {
    setIsDeleting(true)

    DeleteAnnouncement(idx).then(() => {
      Alerts.success('Announcement deleted successfully')
    }).catch((err) => delErrorCheck(err))
      .finally(() => {
        call()
        setSkippedRecords(0)
        setIsDeleting(false)
      })
  }

  // Handler for clicking a message (edit)
  const handleSelect = idx => {
    setSelectedIdx(idx)
    setShowNewAnnouncement(false)
  }

  // Handler for new announcement
  const handleNewAnnouncement = () => {
    setSelectedIdx(null)
    setShowNewAnnouncement(true)
  }

  return (
    <div className='relative'>
      <Loading overlay visible={isCalling && !data?.announcements?.length} />
      <Inline alignX="between" alignY="baseline" className="pb-3" width="full">
        <div className="text-lg font-semibold">Announcements</div>
      </Inline>
      <div className='sm:flex-col md:flex-row md:flex gap-8 mt-4'>
        <div className='sm:w-full md:w-5/12'>
          <Inline alignX="between" width="full">
            <Inline alignY="center" space="xs">
              <div className="text-md">Sent Announcements</div>
            </Inline>
            <div>
              <Button
                onClick={handleNewAnnouncement}
                className='w-60'
                variant="main"
              >
                + New Announcement
              </Button>
            </div>
          </Inline>
          <Card className='mt-4 px-0 py-1 border border-gray-200 rounded-lg'>
            <div className='flex gap-4 px-4 py-2 h-7'>
              <div className="w-2/3">
                <div>Title</div>
              </div>
              <div className="w-1/3">
                <div>Sent</div>
              </div>
            </div>
            <div className=' flex flex-col gap-1'>
              {data?.announcements && data.announcements.length === 0 ? (
                <div className="text-center text-gray-400 py-6">No announcements found.</div>
              ) : (
                data?.announcements && data.announcements.map((msg, idx) => (
                    <SingleAnnouncementHistory
                      key={msg.id}
                      msg={msg}
                      onClick={() => handleSelect(msg.id)}
                      onDelete={() => {
                        handleDelete(msg.id)
                      }}
                      isDeleting={isDeleting}
                    />
                  )
                )
              )}
            </div>
            <div>
              <Pagination
                recordsPerPage={recordsPerPage}
                skippedRecords={skippedRecords}
                onPageClick={setSkippedRecords}
                showDescription={false}
                totalRecords={data?.total || 0}
              />
            </div>
          </Card>
        </div>
        <div className='sm:w-full md:w-7/12'>
          <Card className='mt-4 px-0 py-1 border border-gray-200 rounded-lg'>
            <div className='flex justify-between px-4 h-7 items-baseline'>
              <div>{selectedIdx !== null ? 'Edit Announcement' : 'New Announcement'}</div>
            </div>
            <Formik
              enableReinitialize
              initialValues={{
                notifyPositionIds: selectedMessage ? selectedMessage.positions : [],
                title: selectedMessage ? selectedMessage.title : '',
                body: selectedMessage ? selectedMessage.body : ''
              }}
              onSubmit={handleSave}
            >
              {({handleSubmit, resetForm}) => (
                <form onSubmit={handleSubmit}>
                  <div className='flex flex-col gap-3 px-4 py-3 bg-white'>
                    <div className='flex flex-col gap-2 '>
                      <FormikSelectPositions
                        label="Positions"
                        multi
                        name="notifyPositionIds"
                      />
                      <FormikText name="title" label="Title" />
                      <FormikTextarea label="Body" name="body" minRows={6} />
                    </div>
                    <div className='flex justify-end gap-2'>
                      {selectedIdx !== null && (
                        <>
                          <Button
                            type="button"
                            className='w-32'
                            variant="secondary"
                            onClick={() => {
                              setSelectedIdx(null)
                              resetForm()
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            className='w-32'
                            variant="danger"
                            onClick={() => handleDelete(selectedIdx)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                      <Button
                        type="submit"
                        className='w-60'
                        variant="main"
                      >
                        {selectedIdx !== null ? 'Save Changes' : '+ Send Announcements'}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </Card>
        </div>
      </div>

    </div>
  )
}

export {AnnouncementsIndex}